import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Building2, Edit, Trash2, Star, MapPin, Layers, MessageSquare, FilterX,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import {
  PageHeader, StatsCard, StatsGrid, AppButton, ConfirmModal, StatusBadge,
  FilterToolbar, AppDataTable, ActionMenu, ActionMenuItem, ActionMenuDivider,
  applyColumnFilters, sortRows, isColumnFilterActive,
} from "../ui";
import { useBranch } from "../../contexts/BranchContext";
import { hasPermission } from "../../utils/permissions";

/**
 * BranchesPage — every branch of the institute.
 *
 * The table is `ui/AppDataTable` with the header controls on, the same shape as
 * `/sa/institutes`: filtering, sorting and paging all happen in the browser
 * over the whole list, because the endpoint already returns the whole list — an
 * institute has branches, not a feed of them.
 *
 * ⚠️ There is deliberately no search box. The Branch column's text filter reads
 * the name, the code AND the city — every field the old search looked at — and
 * a value settable from two places is a value that will eventually disagree
 * with itself.
 */

const PAGE_SIZE = 25;

const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const DASH = <span className="tw-text-content-subtle">—</span>;

/** A count that reads as nothing when it is zero, so the eye lands on the
 *  branches that actually have batches. */
const Count = ({ value }) =>
  value > 0 ? <span className="tw-font-medium tw-tabular-nums">{value}</span> : DASH;

const BranchesPage = () => {
  const navigate = useNavigate();
  const { refetchBranches } = useBranch();
  const canManage = hasPermission("manage_branches");

  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [defaultTarget, setDefaultTarget] = useState(null);

  // Per-column filters, keyed by column: { status: ['active'], batchCount: { min: '1' } }.
  // Owned here rather than by the table, exactly as `sort` is — the table
  // renders the funnels and never decides what they mean.
  const [columnFilters, setColumnFilters] = useState({});
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(1);

  // Stable, because `fetchBranches` closes over it — a fresh function each
  // render would churn its dependency array and refetch on every paint.
  const showToast = useCallback(
    (message, type = "success") => setToast({ show: true, message, type }),
    [],
  );

  const fetchBranches = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/branches", { params: { includeInactive: true } });
      setBranches(data.branches || []);
    } catch {
      showToast("Failed to load branches.", "error");
    }
    setLoading(false);
  }, [showToast]);

  useEffect(() => { fetchBranches(); }, [fetchBranches]);

  const stats = useMemo(() => ({
    total: branches.length,
    active: branches.filter((b) => b.isActive).length,
    totalBatches: branches.reduce((s, b) => s + (b.batchCount || 0), 0),
    totalEnquiries: branches.reduce((s, b) => s + (b.enquiryCount || 0), 0),
  }), [branches]);

  /** Cities present in the data, not a fixed list — a set filter offering
   *  places the institute has no branch in is a filter you stop opening. */
  const cities = useMemo(() => {
    const names = new Set(branches.map((b) => b.city).filter(Boolean));
    return [...names].sort((a, b) => a.localeCompare(b)).map((c) => ({ value: c, label: c }));
  }, [branches]);

  const columns = useMemo(() => [
    {
      key: "name",
      label: "Branch",
      sortable: true,
      filter: { type: "text", placeholder: "Name, code or city…" },
      // Reads all three, because all three are visible in or beside this cell
      // and a text filter that cannot match text the user is looking straight
      // at is a filter they stop trusting. The SORT reads the name alone —
      // which is what the column actually shows.
      filterValue: (b) => `${b.name || ""} ${b.code || ""} ${b.city || ""}`,
      sortValue: (b) => b.name || "",
      render: (b) => (
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-font-medium tw-text-content">{b.name}</span>
          {b.isDefault && (
            <StatusBadge status="info" className="tw-shrink-0">
              <Star size={10} /> Default
            </StatusBadge>
          )}
        </div>
      ),
    },
    {
      key: "code",
      label: "Code",
      sortable: true,
      filter: "text",
      filterValue: (b) => b.code || "",
      render: (b) => (b.code
        ? <span className="tw-font-mono tw-text-[0.75rem] tw-text-content-subtle">{b.code}</span>
        : DASH),
    },
    {
      key: "city",
      label: "City",
      sortable: true,
      filter: { type: "set", options: cities },
      filterValue: (b) => b.city || "",
      render: (b) => b.city || DASH,
    },
    {
      key: "batchCount",
      label: "Batches",
      align: "right",
      sortable: true,
      filter: "number",
      // ⚠️ Zero, never undefined — the number filter rejects a non-finite value,
      // so a branch with no batches would be invisible to its own column filter.
      filterValue: (b) => b.batchCount ?? 0,
      render: (b) => <Count value={b.batchCount || 0} />,
    },
    {
      key: "enquiryCount",
      label: "Enquiries",
      align: "right",
      sortable: true,
      filter: "number",
      filterValue: (b) => b.enquiryCount ?? 0,
      render: (b) => <Count value={b.enquiryCount || 0} />,
    },
    {
      key: "enrollmentCount",
      label: "Enrollments",
      align: "right",
      sortable: true,
      filter: "number",
      filterValue: (b) => b.enrollmentCount ?? 0,
      render: (b) => <Count value={b.enrollmentCount || 0} />,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      filter: { type: "set", options: STATUS_FILTER_OPTIONS },
      // A boolean has no name a set filter can offer, so the accessor gives it
      // one. The badge below reads the same two words.
      filterValue: (b) => (b.isActive ? "active" : "inactive"),
      render: (b) => <StatusBadge status={b.isActive ? "active" : "inactive"} />,
    },
    canManage && {
      key: "actions",
      label: "",
      width: 48,
      pinned: "right",
      // A frozen icon column has no width worth choosing, and dragging the one
      // column holding the table's right edge open is a way to lose the action
      // menu behind the scroll.
      resizable: false,
      // ⚠️ The row is clickable, so every control in this cell must stop the
      // click reaching it — otherwise opening the menu also navigates away.
      render: (b) => (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <ActionMenu label={`Actions for ${b.name}`}>
            <ActionMenuItem onClick={() => navigate(`/branches/edit/${b._id}`)}>
              <Edit size={14} /> Edit
            </ActionMenuItem>
            {!b.isDefault && (
              <ActionMenuItem onClick={() => setDefaultTarget(b)}>
                <Star size={14} /> Set as default
              </ActionMenuItem>
            )}
            {!b.isDefault && <ActionMenuDivider />}
            {!b.isDefault && (
              <ActionMenuItem danger onClick={() => setDeleteTarget(b)}>
                <Trash2 size={14} /> Deactivate
              </ActionMenuItem>
            )}
          </ActionMenu>
        </div>
      ),
    },
  ].filter(Boolean), [cities, navigate, canManage]);

  const filtersOn = Object.values(columnFilters).some(isColumnFilterActive);

  /* Both helpers are `ui/AppDataTable`'s, deliberately: the funnel that says a
     column is filtered and the predicate that filters it have to be one
     decision, and the arrow that says a column is sorted has to read the field
     the funnel filters. */
  const visible = useMemo(
    () => sortRows(applyColumnFilters(branches, columns, columnFilters), columns, sort),
    [branches, columns, columnFilters, sort],
  );

  // A filter or a sort can leave the current page past the end of the results.
  useEffect(() => { setPage(1); }, [columnFilters, sort]);
  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [visible, page],
  );

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/branches/${deleteTarget._id}`);
      showToast("Branch deactivated.");
      setDeleteTarget(null);
      fetchBranches();
      refetchBranches();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to delete branch.", "error");
      setDeleteTarget(null);
    }
  };

  const handleSetDefault = async () => {
    if (!defaultTarget) return;
    try {
      await apiClient.patch(`/branches/${defaultTarget._id}/set-default`);
      showToast(`"${defaultTarget.name}" is now the default branch.`);
      setDefaultTarget(null);
      fetchBranches();
      refetchBranches();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to set default.", "error");
      setDefaultTarget(null);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <PageHeader
        icon={Building2}
        title="Branches"
        description="Manage your institute branches"
      />

      <StatsGrid>
        <StatsCard title="Total Branches" value={stats.total} icon={Building2} iconColor="blue" />
        <StatsCard title="Active" value={stats.active} icon={MapPin} iconColor="emerald" />
        <StatsCard title="Total Batches" value={stats.totalBatches} icon={Layers} iconColor="violet" />
        <StatsCard title="Total Enquiries" value={stats.totalEnquiries} icon={MessageSquare} iconColor="amber" />
      </StatsGrid>

      <AppDataTable
        /* `fill={false}` — this page has no height of its own, so the table
           flows with it rather than fighting the stats row for a share of it. */
        fill={false}
        /* Eight columns on a 1280×720 admin screen: which ones need the room is
           the reader's call, not this file's. `storageKey` makes that call last
           — the widths are per browser, so nothing here has to store it. */
        resizable
        storageKey="branches"
        toolbar={
          <FilterToolbar
            flush
            actions={canManage && (
              <AppButton size="sm" onClick={() => navigate("/branches/add")}>
                <Plus size={16} /> Add Branch
              </AppButton>
            )}
          >
            <span className="tw-text-[0.8125rem] tw-text-content-subtle">
              {visible.length === branches.length
                ? `${branches.length} branch${branches.length === 1 ? "" : "es"}`
                : `${visible.length} of ${branches.length}`}
            </span>
            {/* A funnel in a header is easy to set and easy to forget — this is
                the only thing on screen that says a column is hiding rows. */}
            {filtersOn && (
              <AppButton size="sm" variant="outline" onClick={() => setColumnFilters({})}>
                <FilterX size={14} /> Clear filters
              </AppButton>
            )}
          </FilterToolbar>
        }
        columns={columns}
        rows={pageRows}
        rowKey={(b) => b._id}
        loading={loading}
        sort={sort}
        onSortChange={(key, direction) => setSort({ key, direction })}
        columnFilters={columnFilters}
        onColumnFilterChange={(key, value) =>
          setColumnFilters((prev) => {
            // Cleared filters are REMOVED, not stored as undefined — the
            // "any filters on?" test walks the values, and a key sitting there
            // holding nothing would keep "Clear filters" on screen after the
            // last one was cleared.
            const next = { ...prev };
            if (value === undefined) delete next[key];
            else next[key] = value;
            return next;
          })
        }
        onRowClick={canManage ? (b) => navigate(`/branches/edit/${b._id}`) : undefined}
        emptyMessage={filtersOn ? "No branch matches these filters." : "No branches yet."}
        /* ⚠️ PROPS, not an element — AppDataTable renders `<Paginator
           showSinglePage {...pagination} />` itself. Passed only when there is
           more than one page: the table forces `showSinglePage`, so handing it
           props unconditionally would park a "1 of 1" bar under every short
           list. */
        pagination={totalPages > 1 ? {
          page,
          totalPages,
          total: visible.length,
          pageSize: PAGE_SIZE,
          onChange: setPage,
          infoLabel: "branches",
        } : undefined}
      />

      <ConfirmModal
        show={!!deleteTarget}
        title="Deactivate Branch"
        message={`Are you sure you want to deactivate "${deleteTarget?.name}"? Batches must be reassigned first.`}
        confirmText="Deactivate"
        variant="danger"
        onConfirm={handleDelete}
        onHide={() => setDeleteTarget(null)}
      />

      <ConfirmModal
        show={!!defaultTarget}
        title="Set Default Branch"
        message={`Set "${defaultTarget?.name}" as the default branch for this institute?`}
        confirmText="Set Default"
        variant="info"
        icon={<Star size={22} />}
        onConfirm={handleSetDefault}
        onHide={() => setDefaultTarget(null)}
      />
    </div>
  );
};

export default BranchesPage;
